import { jsPDF } from "jspdf";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { Grid, Icon } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery } from "@apollo/client";
import LogoSL from "assets/images/logo-sl.png";
// import LogoUPRO from "assets/images/logo-upro.png";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import EditableText from "components/EditableText";
import PdfHeader from "../components/pdfHeader";
import { formatDate } from "utils/formatters";
import { formatMoneyPuntoDecimal } from "utils/formatters";
import { Money2Text } from "utils/formatters";

// Aseguramos que formatMoneyPunto esté definido
const formatMoneyPunto = (value) => {
  return formatMoneyPuntoDecimal(value);
};

const textosIniciales = [
  "Villa Mercedes (San Luis), MES de AÑO.",
  "XXXXXXX/XX",
  "....................",
  "Secretario ......",
  "Ing. / CPN / Sr.",
  ", correspondientes al Cuatrimestre del Ciclo Lectivo 2025 ",
  "....................",
  "....................",
  "....................",
  "....................",
  "....................",
  "....................",
];

const NotaSolicitud = () => {
  const documentsRef = useRef(null);
  const { id } = useParams();
  const { lg } = boxShadows;
  const [data, setData] = useState(null);
  const [textArray, setTextArray] = useState(textosIniciales);
  const [editable, setEditable] = useState(true);
  const [getPedido, { loading }] = useLazyQuery(
    gql`
      query getPedidoNotaSolicitud($id: ID) {
        pedidosCompra(filter: { id: $id }) {
          id
          createdUser {
            nombre
            apellido
          }
          tipoCompra
          numeroExpediente
          fechaSolicitud
          licitacionInicio
          licitacionFin
          objetoCompra
          sucursal {
            nombre
            sede {
              nombre
            }
          }
          domicilioEntrega
          justificacion
          responsable {
            nombre
            apellido
            email
          }
          detalles {
            id
            numero
            requierePack
            unidadPack
            materiaPrima {
              id
              nombre
              nombreCompra
              tipoUnidad {
                nombre
                multiplicador
              }
            }
            cantidad
            cantidadVerificada
            observacion
            precioEstimado
            nombreServicio
          }
          firmante
          proveedores {
            documento
            razonSocial
            nombre
            apellido
            habilitado
            user {
              email
            }
          }
        }
      }
    `,
    {
      onCompleted: (data) => {
        console.log("Datos del pedido:", data);
        if (!data?.pedidosCompra?.[0]) return;
        setData(data.pedidosCompra[0]);
      },
    }
  );


  function formatFechaVillaMercedes(fechaISO) {
    if (!fechaISO) return "Villa Mercedes (San Luis), -- de ----.";
    const [year, month] = fechaISO.split("-");
    const meses = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    const mesIndex = parseInt(month, 10) - 1;
    const mes = meses[mesIndex];
    const año = year;
    return `Villa Mercedes (San Luis), ${mes} de ${año}.`;
  }

  useEffect(() => {
    if (id) {
      getPedido({ variables: { id } });
    }
  }, [id, getPedido]);

  useEffect(() => {
    if (data) {
      const newTextArray = [...textArray];
      newTextArray[0] = data.id;
      newTextArray[1] = data.tipoCompra?.toUpperCase() || "COMPRA";
      newTextArray[2] = data.objetoCompra || "...";
      newTextArray[6] = formatFechaVillaMercedes(data.fechaSolicitud);
      newTextArray[8] = formatDate(data.licitacionInicio, "dd/MM/yyyy") || "--/--/--";
      newTextArray[9] = formatDate(data.licitacionFin, "dd/MM/yyyy") || "--/--/--";
      newTextArray[10] = data.domicilioEntrega || "....";
      newTextArray[11] = data.createdUser
        ? `${data.createdUser.nombre} ${data.createdUser.apellido}`
        : "....";
      newTextArray[14] = data.id || "----/--";

      const total = data.detalles.reduce(
        (acc, detalle) => acc + detalle.cantidadVerificada * detalle.precioEstimado,
        0
      );
      const formattedTotal = formatMoneyPuntoDecimal(total.toFixed(2));
      newTextArray[17] = "$ " + formattedTotal;

      newTextArray[18] = data.justificacion || "....";
      newTextArray[19] = data.secretaria?.nombre || "....";
      // newTextArray[20] = data.secretaria?.secretario || "....";
      newTextArray[21] = data.sucursal
        ? `${data.sucursal.nombre} - ${data.sucursal.sede?.nombre || ""}`
        : "....";
      newTextArray[22] = data.firmante || "....";

      setTextArray(newTextArray);
    }
  }, [data]);

  const [firma, setFirma] = useState({
    nombre: "Nombre del Solicitante",
    cargo: "Cargo no definido",
  });

  const firmasMap = {
    "Martin Daniel Franco": "SUBDIRECTOR DE PLANIFICACIÓN Y GESTIÓN ACADÉMICA",
    "ANALIA MARCELA LEYES": "JEFE DE ÁREA DE ALUMNOS Y GRADUADOS",
    "Santiago Alfredo Altamirano":
      "SUBDIRECTOR DE INFRAESTRUCTURA, MANTENIMIENTO Y ADMINISTRACIÓN EDILICIA",
    "Nicolas  Arcay": "SUBDIRECTOR DE COMUNICACIÓN Y SISTEMAS",
    "Cristina Beatriz Gimenez": "JEFA DE SUBAREA EVENTOS, CEREMONIAL Y PROTOCOLO",
    "Armando Cardini Nicolas": "SUBDIRECTOR DE COORDINACION DE CARRERA",
    "Florencia Magali Arias": "SUBDIRECTORA DE CONTROL Y ASESORAMIENTO LEGAL",
    "MARIA EMILIA BALORDI BERNARDI": "JEFA DE AREA DE RECURSOS HUMANOS",
    "Sebastian Leguizamon": "SUBDIRECTOR DE PRESUPUESTO, SISTEMA CONTABLE,TESORERIA Y PATRIMONIO",
    "Jessica Beatriz Zafra": "SUBDIRECTORA DE COMPRAS Y CONTRATACIONES",
    "Barbara Fregeiro": "SUBDIRECTOR DE VINCULACION TERRITORIAL Y COMUNITARIA",
    "Marisa Johana Lucero": "JEFA DE AREA COORDINACION SEDE BUENA ESPERANZA",
    "Georgina Cozzolino": "JEFA DE AREA COORDINACION SEDE TERCER ROTONDA",
    "Edgar Martin Altamirano": "JEFE DE AREA COORDINACION SEDE CENTENARIO",
    "Oscar Horacio Luna": "JEFE DE AREA COORDINACION SEDE SERRANIAS PUNTANAS",
    "Cristian Pecorari": "SECRETARIO GENERAL",
    "Alberto Ledesma": "SECRETARIO ACADÉMICO",
    "Francisco José Oromi": "SECRETARIO DE HACIENDA",
    "Matias Daniel Godoy": "SECRETARIO PRIVADO DE RECTORADO",
    "Meline Tahtagian": "SECRETARIA DE VINCULACION PRODUCTIVA Y TERRITORIAL",
    "HECTOR OMAR TORINO": "SECRETARIO DEL INTERIOR",
  };

  useEffect(() => {
    if (data?.firmante) {
      const nombreFirmante = data.firmante.trim();
      const cargo = firmasMap[nombreFirmante] || "Cargo no definido";
      setFirma({ nombre: nombreFirmante, cargo });
    }
  }, [data]);

  const removeHighlightBeforePrint = () => {
    const elements = document.querySelectorAll(".highlight");
    elements.forEach((el) => {
      el.classList.add("no-print-highlight");
    });
  };

  const handleGeneratePdf = async () => {
    if (!data) {
      alert("No hay datos para generar el PDF.");
      return;
    }

    setEditable(false);
    await new Promise((resolve) => setTimeout(resolve, 500));
    removeHighlightBeforePrint();

    const document = documentsRef.current;
    const pdfWidth = 794;
    const pdfHeight = document.scrollHeight + 10;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: [pdfWidth, pdfHeight],
      hotfixes: ["px_scaling"],
    });

    doc.html(document, {
      x: 0,
      y: 0,
      width: pdfWidth,
      windowWidth: pdfWidth,
      margin: 0,
      html2canvas: {
        scale: 1,
        width: pdfWidth,
        windowWidth: pdfWidth,
        scrollY: 0,
        scrollX: 0,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        dpi: 96,
      },
      callback: (docInstance) => {
        const filename = `exd-pedidoNro-${data.id}-Nota solicitud.pdf`;
        docInstance.save(filename);

        // Restaurar resaltados
        const elements = document.querySelectorAll(".no-print-highlight");
        elements.forEach((el) => {
          el.classList.remove("no-print-highlight");
        });
        setEditable(true);
      },
    });
  };

  // Si está cargando o no hay datos, mostrar mensaje
  if (loading) {
    return (
      <EmpleadoLayout>
        <SoftBox p={3} textAlign="center">
          <SoftTypography variant="body2">Cargando datos del pedido...</SoftTypography>
        </SoftBox>
      </EmpleadoLayout>
    );
  }

  if (!data) {
    return (
      <EmpleadoLayout>
        <SoftBox p={3} textAlign="center">
          <SoftTypography variant="body2" color="error">
            No se encontró el pedido o faltan datos.
          </SoftTypography>
        </SoftBox>
      </EmpleadoLayout>
    );
  }

  const total = data.detalles.reduce(
    (acc, detalle) => acc + detalle.cantidadVerificada * detalle.precioEstimado,
    0
  );
  const totalEnLetras = Money2Text(total).toUpperCase();

  return (
    <EmpleadoLayout>
      <SoftBox
        p={2}
        display="flex"
        alignItems="center"
        flexDirection="column"
        minHeight="100vh"
        height="100%"
      >
        <style>
          {`
            .highlight {
              background-color: yellow !important;
            }
            .no-print-highlight {
              background-color: transparent !important;
            }
            @media print {
              .screen-only {
                display: none !important;
              }
            }
          `}
        </style>

        <div ref={documentsRef}>
          <SoftBox
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "794px",
              backgroundColor: "#ffffff",
              boxShadow: lg,
              px: 6,
            }}
            pb={5}
          >
            <PdfHeader />

            {/* Encabezado */}
            <SoftBox pb={5} textAlign="justify">
              <SoftBox px={1}>
                <SoftTypography variant="h6" textAlign="end">
                  {textArray[6]}
                </SoftTypography>
              </SoftBox>
              <SoftBox px={1}>
                {[
                  "Cristian Pecorari",
                  "Alberto Ledesma",
                  "Francisco José Oromi",
                  "Matias Daniel Godoy",
                  "Meline Tahtagian",
                  "HECTOR OMAR TORINO",
                ].includes(data?.firmante?.trim()) ? (
                  <>
                    <SoftTypography variant="h6" fontWeight="bold">
                      Rector
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      Universidad Provincial de Oficios
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      &quot;EVA PERON&quot;
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      Ing. Rudy Roberto Camera
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      S__________/__________D:
                    </SoftTypography>
                  </>
                ) : (
                  <>
                    <SoftTypography variant="h6" fontWeight="bold">
                      <EditableText
                        tooltip="Secretaria, editable desde Datos Generales del Pedido"
                        content={textArray[19]}
                      />
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      Universidad Provincial de Oficios
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      &quot;EVA PERON&quot;
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      <EditableText
                        tooltip="Ej.: (Ing / CPN / Sr)"
                        editable={editable}
                        content={textArray[4]}
                      />{" "}
                      <EditableText tooltip="Nombre del Secretario" content={textArray[20]} />
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      S__________/__________D:
                    </SoftTypography>
                  </>
                )}
              </SoftBox>
            </SoftBox>

            {/* Cuerpo de la nota */}
            <SoftTypography variant="body2" sx={{ textIndent: "4em" }} pb={2} textAlign="justify">
              Por la presente, se solicita Autorización para :{" "}
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ textIndent: "6em" }} pb={2} textAlign="justify">
              -{" "}
              <EditableText
                tooltip="Objeto de la compra. Editable desde Datos Generales del Pedido"
                content={textArray[2]}
                className="screen-only highlight"
              />{" "}
              de la Universidad Provincial de Oficios “Eva Perón”{" "}
              <EditableText
                tooltip="Curso Cuatrimestral. Ej: (Curso Cuatrimestral “Tapicería, Diseño y Restauración”)"
                editable={editable}
                content={textArray[5]}
              />{" "}
              .
            </SoftTypography>
            <SoftTypography variant="body2" pb={2} sx={{ textIndent: "4em" }} textAlign="justify">
              Que se realiza la presente solicitud en virtud de que{" "}
              <EditableText
                tooltip="Justificación del pedido. Editable desde Datos Generales del Pedido"
                content={textArray[18]}
                className="screen-only highlight"
              />
            </SoftTypography>
            <SoftTypography
              variant="body2"
              pb={2}
              fontWeight="bold"
              sx={{ textIndent: "4em" }}
              textAlign="justify"
            >
              Se advierte que: El pedido debe ser entregado en sede{" "}
              <EditableText
                tooltip="Domicilio de Entrega. Editable desde Datos Generales del Pedido"
                content={textArray[10]}
                className="screen-only highlight"
              />
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ textIndent: "4em" }} textAlign="justify" pb={3}>
              El monto estimado por mencionada contratación asciende a una suma de PESOS{" "}
              <EditableText tooltip="Monto en letras" content={totalEnLetras} /> ({textArray[17]})
              según Solicitud de Gastos correspondiente y Especificaciones Técnicas, que se
              acompañan adjuntas a la presente.
            </SoftTypography>

            <SoftTypography variant="body2" textAlign="end" pb={1}>
              Sin otro motivo, saludo a usted atentamente.
            </SoftTypography>

            {/* Firmas */}
            <SoftTypography variant="body2" fontWeight="bold" textAlign="center" pb={1} mt={5}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={6} textAlign="center">
                  <EditableText tooltip="Nombre del Solicitante" content={textArray[11]} />
                  <SoftTypography
                    variant="caption"
                    fontWeight="regular"
                    display="block"
                    sx={{ mt: -0.5, fontSize: "0.75rem" }}
                  >
                    Solicitante
                  </SoftTypography>
                </Grid>
                <Grid item xs={6} textAlign="center">
                  <EditableText tooltip="Firmante" content={textArray[22]} />
                  <SoftTypography
                    variant="caption"
                    fontWeight="regular"
                    display="block"
                    sx={{ mt: -0.5 }}
                  >
                    {firma.cargo}
                  </SoftTypography>
                </Grid>
              </Grid>
            </SoftTypography>

            {/* Tabla de detalles */}
            <SoftBox
            //  sx={{
            //    position: "relative",
            //    overflow: "hidden",
            //    width: "794px",
            //    backgroundColor: "#ffffff",
            //    boxShadow: lg,
            //    px: 6,
            //  }}
            //  pb={5}
            >
              <PdfHeader />

              <SoftBox display="flex" flexDirection="column" gap={2}>
                <Grid container border="1pt solid black" lineHeight={1}>
                  <Grid
                    item
                    px={6}
                    py={1}
                    xs={12}
                    borderBottom="1pt solid black"
                    textAlign="center"
                  >
                    <SoftTypography variant="caption" fontWeight="bold">
                      SOLICITUD DE GASTOS Y ESPECIFICACIONES TÉCNICAS {" "}
                    </SoftTypography>
                  </Grid>
                </Grid>

                <Grid
                  container
                  border="1pt solid black"
                  sx={{
                    "& > *:not(:last-child)": { borderBottom: "1pt solid black" },
                    "& > *:nth-child(-n+2)": { backgroundColor: "rgba(230, 230, 230)" },
                  }}
                  lineHeight={1}
                >
                  <Grid item xs={12} px={1}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      PRODUCTOS O SERVICIOS REQUERIDOS
                    </SoftTypography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    sx={{ "& > *:not(:last-child)": { borderRight: "1pt solid black" } }}
                  >
                    <Grid item xs={0.7} px={1}>
                      <SoftTypography variant="caption">N° </SoftTypography>
                    </Grid>
                    <Grid item xs={1} px={1}>
                      <SoftTypography variant="caption">Código</SoftTypography>
                    </Grid>
                    <Grid item xs={3.5} px={1}>
                      <SoftTypography variant="caption">Descripción</SoftTypography>
                    </Grid>
                    <Grid item xs={1.2} px={1}>
                      <SoftTypography variant="caption">Cantidad</SoftTypography>
                    </Grid>
                    <Grid item xs={1.2} px={1}>
                      <SoftTypography variant="caption">Unidad</SoftTypography>
                    </Grid>
                    <Grid item xs={2.2} px={1}>
                      <SoftTypography variant="caption">Precio Unitario</SoftTypography>
                    </Grid>
                    <Grid item xs={2.2} px={1}>
                      <SoftTypography variant="caption">Precio Total Referencia</SoftTypography>
                    </Grid>
                    {/* <Grid item xs={0.5} px={1}>
                      <SoftTypography variant="caption">Imagen </SoftTypography>
                    </Grid> */}
                  </Grid>

                  {data.detalles
                    .filter((detalle) => detalle.cantidadVerificada > 0)
                    .map((detalle, index) => (
                      <Grid
                        key={index}
                        item
                        xs={12}
                        container
                        sx={{ "& > *:not(:last-child)": { borderRight: "1pt solid black" } }}
                      >
                        <Grid item xs={0.7} px={1}>
                          <SoftTypography variant="caption">{detalle.numero}</SoftTypography>
                        </Grid>
                        <Grid item xs={1} px={1}>
                          <SoftTypography variant="caption">
                            {detalle.materiaPrima?.id || "0000"}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={3.5} px={1}>
                          <SoftTypography variant="caption">
                            {[detalle.nombreServicio, detalle.materiaPrima?.nombre, detalle.observacion]
                              .filter((e) => e)
                              .join(" | ")}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={1.2} px={1}>
                          <SoftTypography variant="caption">
                            {detalle.cantidadVerificada || "-"}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={1.2} px={1}>
                          <SoftTypography variant="caption">
                            {detalle.materiaPrima?.tipoUnidad?.nombre || "-"}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={2.2} px={1}>
                          <SoftTypography variant="caption">
                            $ {formatMoneyPunto(detalle.precioEstimado || 0)}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={2.2} px={1}>
                          <SoftTypography variant="caption">
                            ${" "}
                            {formatMoneyPunto(
                              detalle.cantidadVerificada * detalle.precioEstimado || 0
                            )}
                          </SoftTypography>
                        </Grid>
                        {/* <Grid
                          item
                          xs={1.5}
                          px={1}
                          py={0.5}
                          alignItems={"center"}
                          display="flex"
                          justifyContent="center"
                        >
                          {detalle.materiaPrima?.imagen ? (
                            <img
                              src={resolveStoredFileUrl(detalle.materiaPrima.imagen)}
                              style={{ width: "70px", height: "70px", objectFit: "cover" }}
                            />
                          ) : (
                            <SoftTypography variant="caption">-</SoftTypography>
                          )}
                        </Grid> */}
                      </Grid>
                    ))}
                </Grid>
                <Grid
                  container
                  border="1pt solid black"
                  sx={{
                    "& > *:not(:last-child)": { borderBottom: "1pt solid black" },
                    "& > *:nth-child(-n+2)": { backgroundColor: "rgba(230, 230, 230)" },
                  }}
                  lineHeight={1}
                >
                  <Grid
                    item
                    xs={12}
                    container
                    sx={{ "& > *:not(:last-child)": { borderRight: "1pt solid black" } }}
                  >
                    <Grid item xs={9.8} px={1}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        TOTAL: Pesos {totalEnLetras}.-
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={2.2} px={1}>
                      <SoftTypography variant="caption">
                        ${" "}
                        {formatMoneyPunto(
                          data.detalles.reduce(
                            (acc, detalle) =>
                              acc + detalle.cantidadVerificada * detalle.precioEstimado,
                            0
                          ) || 0
                        )}
                      </SoftTypography>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} px={1}>
                    <SoftTypography variant="caption">
                      JUSTIFICACION:{data.justificacion}.-
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={12} px={1}>
                    <SoftTypography variant="caption">
                      El pedido debe ser entregado en: {data.domicilioEntrega}.
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={12} px={1}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      {" "}
                      Consultas Técnicas: {data.responsable?.nombre} {data.responsable?.apellido} -{" "}
                      {data.responsable?.email}
                    </SoftTypography>
                  </Grid>
                </Grid>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </div>

        {/* Botón flotante para descargar PDF */}
        <SoftBox
          variant="contained"
          bgColor="white"
          p={2}
          style={{
            position: "fixed",
            bottom: "1.5rem",
            right: "1.5rem",
            zIndex: 1000,
            boxShadow:
              "inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
          }}
        >
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <SoftButton variant="gradient" color="error" onClick={handleGeneratePdf}>
                <Icon fontWeight="bold">picture_as_pdf</Icon>
                &nbsp;&nbsp;Descargar PDF
              </SoftButton>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </EmpleadoLayout>
  );
};

export default NotaSolicitud;
